import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Film, Tv, Gamepad2, Megaphone, Podcast, Music, Search, Loader2, Send, Check, DollarSign, Clock, Filter } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const USE_CASES = [
  { id: "film", label: "Film", icon: Film, color: "text-purple-400" },
  { id: "tv", label: "TV", icon: Tv, color: "text-blue-400" },
  { id: "games", label: "Games", icon: Gamepad2, color: "text-green-400" },
  { id: "ads", label: "Advertising", icon: Megaphone, color: "text-orange-400" },
  { id: "podcasts", label: "Podcasts", icon: Podcast, color: "text-pink-400" },
];

const TIER_INFO = {
  personal: { price: 49, label: "Personal", desc: "YouTube, podcasts, personal projects", color: "border-zinc-500" },
  commercial: { price: 199, label: "Commercial", desc: "Ads, corporate videos, apps", color: "border-blue-500" },
  broadcast: { price: 499, label: "Broadcast", desc: "TV, film, streaming platforms", color: "border-purple-500" },
  exclusive: { price: 999, label: "Exclusive", desc: "Full buyout, unlimited usage", color: "border-neon-yellow" },
};

export default function SyncLicensingPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [selectedBeat, setSelectedBeat] = useState(null);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    tier: "commercial", use_case: "", project_name: "", project_description: "", budget: "", timeline: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const headers = (() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  })();

  useEffect(() => {
    const params = {};
    if (selectedUseCase) params.use_case = selectedUseCase;
    if (searchQuery) params.genre = searchQuery;
    axios.get(`${API}/api/sync/listings`, { params })
      .then(res => setListings(res.data?.listings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedUseCase, searchQuery]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to submit inquiry"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/sync/inquiry`, {
        ...inquiryForm,
        beat_id: selectedBeat?.beat_id,
        beat_title: selectedBeat?.title
      }, { headers });
      toast.success("Inquiry submitted! We'll be in touch.");
      setShowInquiry(false);
      setInquiryForm({ tier: "commercial", use_case: "", project_name: "", project_description: "", budget: "", timeline: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="sync-licensing-page">
      <SEO title="Sync Licensing" description="License beats for film, TV, games, and advertising." />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-purple-500/20 text-purple-400 border-0 mb-3">
            <Film className="w-3 h-3 mr-1" /> Sync Licensing
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-3">
            Sync <span className="text-purple-400">Licensing</span>
          </h1>
          <p className="text-zinc-500 max-w-lg mx-auto text-sm">
            License beats for film, TV, games, ads, and podcasts. Professional quality, cleared for commercial use.
          </p>
        </div>

        {/* Use Case Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <Button variant={!selectedUseCase ? "default" : "outline"} size="sm"
            onClick={() => setSelectedUseCase(null)}
            className={!selectedUseCase ? "bg-purple-500 text-white" : "border-white/10"}
            data-testid="filter-all"
          >All</Button>
          {USE_CASES.map(uc => (
            <Button key={uc.id} variant={selectedUseCase === uc.id ? "default" : "outline"} size="sm"
              onClick={() => setSelectedUseCase(uc.id)}
              className={selectedUseCase === uc.id ? "bg-purple-500 text-white" : "border-white/10"}
              data-testid={`filter-${uc.id}`}
            >
              <uc.icon className="w-3.5 h-3.5 mr-1.5" />{uc.label}
            </Button>
          ))}
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-8 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input placeholder="Search by genre, mood, or keyword..."
            value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setLoading(true); }}
            className="bg-black/50 border-white/10 pl-10" data-testid="sync-search" />
        </div>

        {/* Pricing Tiers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {Object.entries(TIER_INFO).map(([key, tier]) => (
            <div key={key} className={`glass border-2 ${tier.color} rounded-xl p-4 text-center`} data-testid={`tier-card-${key}`}>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{tier.label}</p>
              <p className="text-2xl font-heading text-white">${tier.price}<span className="text-xs text-zinc-500">+</span></p>
              <p className="text-[10px] text-zinc-500 mt-1">{tier.desc}</p>
            </div>
          ))}
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-400" /></div>
        ) : listings.length === 0 ? (
          <div className="glass border border-white/10 rounded-xl p-16 text-center max-w-md mx-auto">
            <Music className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 mb-2">No sync listings found</p>
            <p className="text-zinc-600 text-xs">Try adjusting your filters or check back soon</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map((beat, idx) => (
              <motion.div key={beat.beat_id}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                className="glass border border-white/10 rounded-xl p-5 hover:border-purple-500/30 transition cursor-pointer"
                onClick={() => { setSelectedBeat(beat); setShowInquiry(true); }}
                data-testid={`sync-listing-${beat.beat_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-white text-sm">{beat.title}</h3>
                    <p className="text-[11px] text-zinc-500">{beat.producer_name || "Producer"}</p>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">
                    {beat.genre || "Beat"}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-zinc-500 mb-3">
                  {beat.bpm && <span>{beat.bpm} BPM</span>}
                  {beat.key && <span>Key: {beat.key}</span>}
                  {beat.mood && <span>{beat.mood}</span>}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-purple-400 font-medium">From ${TIER_INFO.personal.price}</span>
                  <Button size="sm" variant="outline" className="border-purple-500/30 text-purple-400 text-xs h-7 hover:bg-purple-500/10"
                    data-testid={`license-btn-${beat.beat_id}`}
                  >
                    License
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Inquiry Dialog */}
        <Dialog open={showInquiry} onOpenChange={setShowInquiry}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-lg" data-testid="sync-inquiry-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">
                License: {selectedBeat?.title || "Beat"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInquiry} className="space-y-4">
              <div>
                <Label>License Tier</Label>
                <Select value={inquiryForm.tier} onValueChange={v => setInquiryForm({...inquiryForm, tier: v})}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIER_INFO).map(([k, t]) => (
                      <SelectItem key={k} value={k}>{t.label} — ${t.price}+</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Use Case</Label>
                <Select value={inquiryForm.use_case} onValueChange={v => setInquiryForm({...inquiryForm, use_case: v})}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="use-case-select">
                    <SelectValue placeholder="Select use case" />
                  </SelectTrigger>
                  <SelectContent>
                    {USE_CASES.map(uc => (
                      <SelectItem key={uc.id} value={uc.id}>{uc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Project Name</Label>
                <Input value={inquiryForm.project_name} onChange={e => setInquiryForm({...inquiryForm, project_name: e.target.value})}
                  className="bg-black/50 border-white/10" required data-testid="project-name-input" />
              </div>
              <div>
                <Label>Project Description</Label>
                <Textarea value={inquiryForm.project_description} onChange={e => setInquiryForm({...inquiryForm, project_description: e.target.value})}
                  className="bg-black/50 border-white/10" rows={3} data-testid="project-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Budget Range</Label>
                  <Input value={inquiryForm.budget} onChange={e => setInquiryForm({...inquiryForm, budget: e.target.value})}
                    className="bg-black/50 border-white/10" placeholder="$500-$2000" data-testid="budget-input" />
                </div>
                <div>
                  <Label>Timeline</Label>
                  <Input value={inquiryForm.timeline} onChange={e => setInquiryForm({...inquiryForm, timeline: e.target.value})}
                    className="bg-black/50 border-white/10" placeholder="2 weeks" data-testid="timeline-input" />
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-purple-500 text-white font-bold" data-testid="submit-inquiry-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Inquiry
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
